/**
 * lib/zipCentroid — pure-GeoJSON helpers for Phase F.2 ZIP search.
 *
 * Privacy contract: this module operates EXCLUSIVELY on ZIP polygon
 * data already shipped to the client (the public GeoJSON file +
 * the already-public `coverage_zips` set). It never reads, fetches,
 * stores, or transmits any address-shaped field. The nearest-covered
 * suggestion is computed as a centroid-to-centroid Haversine — no
 * partner location, no booking address, no real-time signal.
 *
 * Exports:
 *   - centroidOfFeature(feature)  → {lat, lng} | null
 *   - nearestCoveredZip({query, coveredZips, geojson}) → "68130" | null
 *
 * All math is great-circle in km on a unit sphere; we do NOT need
 * ellipsoid-accurate distance for "nearest neighbor among 30 ZIPs."
 */

const EARTH_RADIUS_KM = 6371.0088;
const _toRad = (deg) => (deg * Math.PI) / 180;


/**
 * Polygon centroid via the average-of-vertices approximation.
 * Sufficient for ZIP-bucket nearest-neighbor (we only need a
 * representative point inside / near the polygon; a true centroid
 * would require area-weighted integration which we don't need at
 * this precision).
 */
export function centroidOfFeature(feature) {
  if (!feature || !feature.geometry) return null;
  const { type, coordinates } = feature.geometry;
  if (!Array.isArray(coordinates)) return null;
  // Collect rings from Polygon or MultiPolygon
  let rings;
  if (type === "Polygon") {
    rings = coordinates;
  } else if (type === "MultiPolygon") {
    // Use the largest ring's outer boundary as the representative shape.
    rings = (coordinates[0] || []);
  } else {
    return null;
  }
  const outer = rings[0] || [];
  if (outer.length === 0) return null;
  let sumLng = 0;
  let sumLat = 0;
  let n = 0;
  for (const [lng, lat] of outer) {
    if (Number.isFinite(lng) && Number.isFinite(lat)) {
      sumLng += lng;
      sumLat += lat;
      n += 1;
    }
  }
  if (n === 0) return null;
  return { lat: sumLat / n, lng: sumLng / n };
}


function _haversineKm(a, b) {
  if (!a || !b) return Infinity;
  const dLat = _toRad(b.lat - a.lat);
  const dLng = _toRad(b.lng - a.lng);
  const lat1 = _toRad(a.lat);
  const lat2 = _toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
}


/**
 * Find the closest ZIP in `coveredZips` to the polygon centroid of
 * the user-typed `query` ZIP. Returns null if:
 *   - `geojson` is missing / has no features
 *   - The query ZIP doesn't appear in the GeoJSON (we have no
 *     centroid to anchor against)
 *   - No covered ZIPs in the GeoJSON (cold map)
 *
 * Pure function: same inputs → same output. Safe to call repeatedly.
 */
export function nearestCoveredZip({ query, coveredZips, geojson }) {
  if (!geojson || !Array.isArray(geojson.features)) return null;
  const covered = coveredZips instanceof Set
    ? coveredZips
    : new Set(coveredZips || []);
  if (covered.size === 0) return null;
  // Find the query feature
  const queryFeat = geojson.features.find(
    (f) => f && f.properties && f.properties.zip === query,
  );
  const queryCentroid = centroidOfFeature(queryFeat);
  if (!queryCentroid) return null;
  // Score each covered ZIP's centroid against the query centroid
  let bestZip = null;
  let bestDist = Infinity;
  for (const feat of geojson.features) {
    const z = feat?.properties?.zip;
    if (!z || !covered.has(z)) continue;
    const c = centroidOfFeature(feat);
    if (!c) continue;
    const d = _haversineKm(queryCentroid, c);
    if (d < bestDist) {
      bestDist = d;
      bestZip = z;
    }
  }
  return bestZip;
}
